import { Box } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import type { DockerContainer, DockerContainerState } from '../../types';

const stateTone: Record<DockerContainerState, 'success' | 'danger' | 'warning' | 'neutral'> = {
  running: 'success',
  exited: 'danger',
  dead: 'danger',
  restarting: 'warning',
  paused: 'warning',
  created: 'neutral',
};

export function ContainerTable({ containers, loading }: { containers: DockerContainer[]; loading?: boolean }) {
  if (!loading && containers.length === 0) {
    return <EmptyState icon={Box} title="No containers found" />;
  }

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800">
            <th className="py-2.5 pr-4 font-medium">Container</th>
            <th className="py-2.5 pr-4 font-medium">Image</th>
            <th className="py-2.5 pr-4 font-medium">State</th>
            <th className="py-2.5 pr-4 font-medium">Status</th>
            <th className="py-2.5 pr-4 font-medium">Ports</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5} className="py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            : containers.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3 pr-4 font-medium text-slate-800 dark:text-slate-200">{c.name}</td>
                  <td className="py-3 pr-4 max-w-[220px] truncate text-slate-500 dark:text-slate-400">{c.image}</td>
                  <td className="py-3 pr-4">
                    <Badge tone={stateTone[c.state]}>{c.state}</Badge>
                  </td>
                  <td className="py-3 pr-4 text-slate-400">{c.status}</td>
                  <td className="py-3 pr-4 text-slate-400">
                    {c.ports.length === 0
                      ? '-'
                      : c.ports
                          .filter((p) => p.publicPort)
                          .map((p) => `${p.publicPort}:${p.privatePort}`)
                          .join(', ') || 'internal'}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
